import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../Utils/api";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiExternalLink,
  FiKey,
  FiMail,
  FiSend,
} from "react-icons/fi";
import "./EmailNotificacao.css";


const TestEmailModal = ({ show, handleClose, configId }) => {
  const [email, setEmail]             = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [loading, setLoading]         = useState(false);
  const [resultado, setResultado]     = useState(null); // { tipo: "sucesso"|"erro", mensagem, helpUrl, detail }

  // Limpa o estado ao reabrir
  useEffect(() => {
    if (show) {
      setEmail("");
      setIsEmailValid(true);
      setResultado(null);
    }
  }, [show]);

  const validateEmail = (value) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsEmailValid(regex.test(value));
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    validateEmail(e.target.value);
    setResultado(null);
  };

  const handleSubmit = async () => {
    if (!isEmailValid || !email || !configId) return;

    setLoading(true);
    setResultado(null);

    try {
      const token      = localStorage.getItem("authToken");
      const entidadeId = localStorage.getItem("entidade_id");
      const headers    = { "Content-Type": "application/json" };
      if (token)      headers.Authorization = `Bearer ${token}`;
      if (entidadeId) headers.id_entidade   = entidadeId;

      const response = await fetch(
        `${BASE_URL}/api/emailNotificacao/TestarConfiguracaoEmail/${encodeURIComponent(email)}`,
        { method: "POST", headers, body: JSON.stringify({ id: configId }) }
      );

      const data = await response.json();

      if (response.ok) {
        setResultado({ tipo: "sucesso", mensagem: data.message || "Email de teste enviado com sucesso!" });
      } else {
        setResultado({
          tipo:     "erro",
          mensagem: data.message || "Falha ao enviar email de teste.",
          helpUrl:  data.helpUrl  || null,
          detail:   data.detail   || null,
          provider: data.provider || null,
        });
      }
    } catch (err) {
      setResultado({
        tipo:     "erro",
        mensagem: "Erro de conexão com o servidor. Tente novamente.",
        helpUrl:  null,
        detail:   err.message || null,
      });
    } finally {
      setLoading(false);
    }
  };

  const podeEnviar = isEmailValid && email.trim() !== "" && !loading;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName="email-test-dialog"
      contentClassName="email-test-modal"
    >
      <Modal.Header closeButton className="email-test-header">
        <div className="email-test-header-main">
          <div className="email-test-header-icon">
            <FiSend size={19} aria-hidden="true" />
          </div>
          <div>
            <Modal.Title>Testar configuração</Modal.Title>
            <p>Envie uma mensagem para confirmar as credenciais e a conexão.</p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="email-test-body">
        <Form>
          <Form.Group controlId="formEmailTeste">
            <Form.Label>Email de destino</Form.Label>
            <Form.Control
              type="email"
              placeholder="nome@empresa.com.br"
              value={email}
              onChange={handleEmailChange}
              isInvalid={!isEmailValid && email !== ""}
              disabled={loading}
              autoFocus
            />
            {!isEmailValid && email !== "" && (
              <Form.Control.Feedback type="invalid">
                Informe um endereço de e-mail válido.
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Form>

        {/* Resultado: Sucesso */}
        {resultado?.tipo === "sucesso" && (
          <Alert variant="success" className="email-test-result">
            <FiCheckCircle size={19} aria-hidden="true" />
            <div>
              <strong>Enviado com sucesso!</strong>
              <div className="email-test-result-message">{resultado.mensagem}</div>
            </div>
          </Alert>
        )}

        {/* Resultado: Erro */}
        {resultado?.tipo === "erro" && (
          <Alert variant="danger" className="email-test-result">
            <FiAlertCircle size={19} aria-hidden="true" />
            <div className="flex-grow-1">
              <div>
                <strong>Falha no envio</strong>
                <div className="email-test-result-message">{resultado.mensagem}</div>
              </div>

              {resultado.provider && (
                <div className="email-test-provider-help">
                  <div className="email-test-provider-title">
                    <FiKey aria-hidden="true" />
                    {resultado.provider === "google"
                      ? "Gmail — Senha de App necessária"
                      : "Microsoft 365 — opções de autenticação"}
                  </div>
                  <p>
                    {resultado.provider === "google"
                      ? "O Google pode exigir uma Senha de App de 16 dígitos quando a verificação em duas etapas estiver ativa."
                      : "Verifique o SMTP AUTH da caixa de correio ou utilize OAuth2 Microsoft Graph."}
                  </p>
                  {resultado.helpUrl && (
                    <a href={resultado.helpUrl} target="_blank" rel="noopener noreferrer">
                      Abrir instruções do provedor <FiExternalLink />
                    </a>
                  )}
                </div>
              )}

              {resultado.detail && (
                <div className="email-test-detail">
                  <strong>Detalhe técnico:</strong> {resultado.detail}
                </div>
              )}

              {!resultado.provider && resultado.helpUrl && (
                <a
                  href={resultado.helpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="email-test-help-link"
                >
                  Mais informações <FiExternalLink />
                </a>
              )}
            </div>
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer className="modal-footer-padrao email-test-footer">
        <div className="modal-footer-acoes-esquerda">
          <FiMail size={15} aria-hidden="true" />
          <span>Nenhuma configuração será alterada durante o teste.</span>
        </div>
        <div className="modal-footer-acoes-direita">
          <Button variant="light" className="modal-footer-btn-cancelar" onClick={handleClose} disabled={loading}>
            Fechar
          </Button>
          <Button
            variant="success"
            className="modal-footer-btn-salvar"
            onClick={handleSubmit}
            disabled={!podeEnviar}
          >
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" style={{ marginRight: 6 }} />
                Enviando…
              </>
            ) : (
              "Enviar Teste"
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default TestEmailModal;
